import { Instance } from 'mobx-state-tree'
import { QuestionApi } from '../services/api-objects/QuestionApi'
import { isQuestion } from '../type-guards/isQuestion'
import { Question } from '../models/Question'
import { getChoicesByQuestionType } from '../utils/getChoicesByQuestionType'
import { QuestionType } from '../models/QuestionType'
import { Factoid } from '../models/Factoid'
import { QuestionChoice } from '../models/QuestionChoice'

const _startCase = require('lodash/startCase')

import { uniq } from 'ramda'
const _has = require('lodash/has')

const formatTF = (text: string) => {
  if (text.trim().toLocaleLowerCase() === 'true' || text?.trim().toLocaleLowerCase() === 't') {
    return 'True'
  } else if (
    text.trim().toLocaleLowerCase() === 'false' ||
    text?.trim().toLocaleLowerCase() === 'f'
  ) {
    return 'False'
  } else {
    return text
  }
}

let rootStore
export class QuestionHelper {
  static setRootStore = (store: any) => {
    rootStore = store
  }

  static importQuestions = (data) => {
    const questions = data.map((row) => {
      const advanced = [
        'choice_1_feedback',
        'choice_2_feedback',
        'choice_3_feedback',
        'choice_4_feedback',
      ].some((h) => _has(row, h))

      const factoid = ['general_feedback'].some((h) => _has(row, h))
        ? Factoid.create({ factoid_id: null, text: row.general_feedback || row.about_the_answer })
        : null

      const { shuffle_answers, reinforce, external_id } = row

      const choice_1 = row.choice_1
      const choice_2 = row.choice_2
      const choice_3 = row.choice_3
      const choice_4 = row.choice_4

      // 1. If no type found, start with Multiple Choice - if the content is an info card return
      let question_type = row.question_type ? row.question_type : 'multiple_choice'

      if (question_type === 'Info Card') {
        const card = QuestionHelper.createNewQuestion('Card', {
          card_markdown: row.choice_1,
          card_name: row.question,
          original_name: row.question,
          touched: true,
        })
        return card
      }

      // 2. Format answer key (a,b,c,d or 1,2,3,4) to 0 index
      let correct = [] as any
      const correct_choices = row.correct_choices || row.key
      try {
        correct =
          correct_choices
            ?.replace(/\s+/g, '')
            ?.split(',')
            ?.map((k) => {
              if (k.toLowerCase() === 'a') {
                return 0
              } else if (k.toLowerCase() === 'b') {
                return 1
              } else if (k.toLowerCase() === 'c') {
                return 2
              } else if (k.toLowerCase() === 'd') {
                return 3
              } else if (parseInt(k) - 1 >= 0 && parseInt(k) - 1 <= 4) {
                return parseInt(k) - 1
              }
            }) ?? []
      } catch (e) {
        console.warn('key not formatted properly')
      }

      if (correct?.length) {
        correct = uniq(correct)?.filter((a) => a !== undefined)
      }

      // 3. regex check for -> to override type to matching
      if (
        [row.choice_1, row.choice_2, row.choice_3, row.choice_4].some(
          (c) => c && /^(.+)->(.+)/.test(c),
        )
      ) {
        question_type = 'matching'
      }

      // 4. Build choices
      let choices = [] as any
      let choice: any
      switch (question_type?.trim().toLocaleLowerCase()) {
        case 'matching':
          choice = QuestionChoice.create({ correct: true, pairs: [] })
          ;[choice_1, choice_2, choice_3, choice_4]
            .filter((c) => Boolean(c?.trim()))
            .map((c) => {
              const p = c.split('->')
              if (p?.length === 2) {
                choice.setPairs(
                  choice.pairs?.length
                    ? [...choice.pairs, { left: p[0].trim(), right: p[1].trim(), touched: true }]
                    : [{ left: p[0].trim(), right: p[1].trim(), touched: true }],
                )
              }
            })
          // Pad to at least 2 pairs so the editor has rows ready when the import couldn't extract any.
          while ((choice.pairs?.length ?? 0) < 2) {
            choice.setPairs([...(choice.pairs ?? []), { left: '', right: '', touched: true }])
          }
          choices.push(choice)
          break
        case 'ordering':
          choice = QuestionChoice.create({ correct: true, ordering: [] })
          choice.setOrdering(
            [choice_1, choice_2, choice_3, choice_4]
              .filter((c) => Boolean(c?.trim()))
              .map((c, index) => ({ index, text: c, touched: true })),
          )
          choices.push(choice)
          break
        case 'open_text':
          choices = [choice_1, choice_2, choice_3, choice_4]
            .filter((c) => Boolean(c?.trim()))
            .map((c) =>
              QuestionChoice.create({
                touched: true,
                correct: true,
                text: c,
              }),
            )
          break
        default:
          choices = [choice_1, choice_2, choice_3, choice_4]
            .filter((c) => Boolean(c?.trim()))
            .map((text, index) => {
              return QuestionChoice.create({
                text,
                touched: true,
                correct: correct.includes(index),
                factoid: advanced
                  ? Factoid.create({
                      factoid_id: null,
                      text: row[`choice_${index + 1}_feedback`] ?? '',
                    })
                  : null,
              })
            })
          break
      }

      // 5. If only two choices and they are loosly t / f formatted - change to T/F type
      if (
        choices?.length === 2 &&
        choices.every((c) =>
          ['true', 'false', 'yes', 'no', 't', 'f'].includes(c?.text?.trim().toLowerCase()),
        )
      ) {
        question_type = 'true_or_false'
        choices.map((c) => {
          c.setText(formatTF(c.text))
        })
      }

      // 6. if no correct_choices && question_type MC / TF default correct to first choice
      if (
        ['multiple_choice', 'true_or_false'].includes(question_type) &&
        choices.every((c) => !c.correct)
      ) {
        choices[0].set('correct', true)
      }

      const shuffle = shuffle_answers?.trim().toLowerCase() === 'true' || false
      const adaptive_eligible = reinforce?.toLowerCase() === 'true' || !reinforce

      return Question.create({
        advanced,
        adaptive_eligible,
        difficulty: 0,
        factoid,
        is_locked: false,
        question_id: null,
        question_type:
          question_type === 'true_or_false' ? 'True or False' : _startCase(question_type?.trim()),
        external_id,
        choices,
        text: row.question || '',
        alt_text: '',
        image_src: null,
        keep_answer_order: !shuffle,
        shuffle: shuffle,
        touched: true,
      })
    })

    // 7. Mark any questions without warnings / errors as approved
    questions.map((question) => {
      if (question.allErrors?.length === 0 && question.allWarnings?.length === 0) {
        question.set('approved', true)
      }
    })

    return questions
  }

  static loadQuestion = async (id: number, edit?: boolean) => {
    const questionResponse = await QuestionApi.getQuestion(id)

    if (!questionResponse.ok || !questionResponse.data) {
      throw new Error('Failed to loadQuestion')
    }

    let choices = questionResponse.data.answers_summary
    if (questionResponse.data.question_type === 'Open Text' && edit) {
      choices = questionResponse.data.answers_summary.filter((c) => c.correct)
    }

    return Question.create({
      advanced: Boolean(questionResponse.data.answers_summary?.some((choice) => choice.factoid)),
      adaptive_eligible: questionResponse.data.adaptive_eligible,
      order: questionResponse.data.order,
      difficulty: questionResponse.data.difficulty ?? 0,
      factoid: questionResponse.data.factoid ?? null,
      is_locked: false,
      question_id: questionResponse.data.question_id,
      question_type: questionResponse.data.question_type,
      choices,
      text: questionResponse.data.text,
      alt_text: questionResponse.data.alt_text ?? '',
      image_src: questionResponse.data.image_url ?? '',
      keep_answer_order: questionResponse.data.keep_answer_order ?? false,
      approved: true,
      external_id: questionResponse.data.external_id ?? '',
      case_sensitive: questionResponse.data.case_sensitive,
    })
  }

  /**
   * Creates a new question with defaults.
   *
   * @param questionType Optional question type to create
   */
  static createNewQuestion = (questionType?: Instance<typeof QuestionType>, init?: any) => {
    return Question.create({
      advanced: false,
      order: 0,
      adaptive_eligible: true,
      difficulty: 0,
      factoid: null,
      is_locked: false,
      question_id: null,
      question_type: questionType ?? 'Multiple Choice',
      choices: getChoicesByQuestionType(questionType ?? 'Multiple Choice'),
      text: '',
      alt_text: '',
      image_src: null,
      keep_answer_order: false,
      fact_id: null,
      documentversion_id: null,
      generated_question_id: null,
      touched: false,
      ...init,
    })
  }

  static objectsToQuestions(questionObjects: Array<any>): Array<Instance<typeof Question>> {
    if (!Array.isArray(questionObjects)) {
      console.warn('QuestionHelper.objectsToQuestions was passed a non array arg', questionObjects)
      return [] as Array<Instance<typeof Question>>
    }
    if (!questionObjects || questionObjects.length === 0) {
      console.warn(
        'QuestionHelper.objectsToQuestions was passed a null or 0 length array',
        questionObjects,
      )
      return [] as Array<Instance<typeof Question>>
    }
    return questionObjects.map((q: any) => {
      if (isQuestion(q)) {
        return q
      } else {
        return Question.create({
          // @ts-ignore
          ...q,
          approved: true,
          touched: true,
        })
      }
    })
  }

  static publishImage = async (q: Instance<typeof Question>) => {
    if (q.question_id && q.imageFile) {
      const response = await QuestionApi.publishImage(q.question_id, q.imageFile)
      return response.ok
    } else if (q.image) {
      // do nothing
    } else if (q.question_id && q.imageChanged) {
      const response = await QuestionApi.removeImage(q.question_id)
      return response.ok
    }
  }

  static removeImage = async (id: number) => {
    const response = await QuestionApi.removeImage(id)

    return response.ok
  }
}
